import { AlertTriangle, Calculator, CheckCircle2, ClipboardList, FileSearch, SearchX, Sparkles } from 'lucide-react';
import EmptyState from '@/components/common/EmptyState.jsx';
import ClienteCombobox from '@/components/ui/ClienteCombobox.jsx';
import { buttonClass, fieldClass, LabeledField, money, primaryButtonClass, shellCard } from './shared.jsx';

function MetricCard({ label, value, tone = 'default' }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 ${tone === 'brand' ? 'border-[rgba(var(--brand),0.25)] bg-[rgba(var(--brand),0.08)]' : 'border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)]'}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">{label}</p>
      <p className="mt-2 text-lg font-semibold text-[rgb(var(--fg))]">{value}</p>
    </div>
  );
}

function TimelineItem({ item, index }) {
  const tone = item.status === 'done'
    ? 'border-[rgba(var(--brand),0.22)] bg-[rgba(var(--brand),0.08)]'
    : 'border-[rgba(var(--danger),0.18)] bg-[rgba(var(--danger),0.06)]';

  return (
    <li className={`rounded-2xl border p-4 ${tone}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[rgba(var(--muted),0.95)]">Paso {index + 1}</p>
      <p className="mt-1 text-sm font-semibold text-[rgb(var(--fg))]">{item.title || item.step}</p>
      <p className="mt-2 text-sm text-[rgba(var(--fg),0.84)]">{item.detail}</p>
    </li>
  );
}

export default function SimulatorPanel({
  form,
  selectedCliente,
  onChange,
  onClienteSelect,
  onRun,
  onReset,
  simulation,
  context,
  isRunning = false,
  errorMessage = '',
  onRetry,
}) {
  const versionLabel = simulation?.versionPrincipal
    ? `${simulation.versionPrincipal.listaCodigo || 'Lista'} / v${simulation.versionPrincipal.versionNumero || '-'}`
    : context?.activeVersionLabel || 'Sin versión resuelta todavía';
  const ruleLabel = context?.ruleCode || context?.ruleName
    ? `${context?.ruleCode || 'Regla'}${context?.ruleName ? ` · ${context.ruleName}` : ''}`
    : 'Conjunto de reglas vigentes';
  const diagnostic = simulation?.diagnostic || null;
  const hasValidCliente = Number.isInteger(Number(form?.clienteId)) && Number(form.clienteId) > 0;
  const canRetry = Boolean(onRetry && hasValidCliente);

  return (
    <section className="space-y-4">
      <header className={`${shellCard} p-5`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[rgba(var(--muted),0.95)]">Simulación operativa</p>
            <h2 className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">Probá la valorización sin salir del circuito de Tarifas</h2>
            <p className="mt-2 max-w-3xl text-sm text-[rgba(var(--fg),0.82)]">
              El simulador usa datos de guía para mostrar lista resuelta, reglas que matchearon, descartes y el detalle por concepto que después debería quedar persistido en el snapshot valorizado.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:min-w-[360px]">
            <MetricCard label="Lista / versión" value={versionLabel} />
            <MetricCard label="Evaluación" value={ruleLabel} />
          </div>
        </div>
        <div className="mt-4 rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] px-4 py-3 text-sm text-[rgba(var(--fg),0.84)]">
          Circuito real del motor: <strong>Concepto</strong> → <strong>Numerador</strong> → <strong>Lista</strong> → <strong>Versión</strong> → <strong>Asignación</strong> → <strong>Regla</strong> → <strong>Simulación</strong> → <strong>Snapshot</strong>.
          <span className="mt-1 block font-semibold text-[rgb(var(--brand))]">Esta operación es de sólo lectura: no crea ni modifica guías, reglas o snapshots.</span>
        </div>
      </header>

      {errorMessage ? (
        <div className="rounded-2xl border border-[rgba(var(--danger),0.24)] bg-[rgba(var(--danger),0.08)] p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-[rgb(var(--danger))]" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-[rgb(var(--fg))]">Revisá los datos de simulación</p>
                <p className="mt-1 text-sm text-[rgba(var(--fg),0.82)]">{errorMessage}</p>
              </div>
            </div>
            {canRetry ? <button type="button" className={buttonClass} onClick={onRetry}>Reintentar</button> : null}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-12 gap-4">
        <section className={`${shellCard} col-span-12 p-5 xl:col-span-5`}>
          <div className="flex items-center gap-2 text-base font-semibold text-[rgb(var(--fg))]">
            <Calculator className="h-5 w-5 text-[rgb(var(--brand))]" aria-hidden="true" />
            Inputs de prueba
          </div>
          <p className="mt-1 text-sm text-[rgba(var(--muted),0.95)]">
            Reproducí una guía real para validar qué regla gana por concepto y por qué.
          </p>
          <div className="mt-4 grid grid-cols-12 gap-4">
            <LabeledField label="Fecha de vigencia" hint="La resolución de versión depende de la fecha." span="col-span-12 md:col-span-6">
              <input type="date" className={fieldClass} value={form.fecha} onChange={(e) => onChange('fecha', e.target.value)} />
            </LabeledField>
            <LabeledField label="Cliente" hint="Dato obligatorio para simular." span="col-span-12 md:col-span-6">
              <div className="mt-1">
                <ClienteCombobox
                  id="tariff-simulator-cliente"
                  name="clienteId"
                  value={form.clienteId || ''}
                  selectedCliente={selectedCliente}
                  onChange={(event) => {
                    const nextValue = Number(event?.target?.value);
                    onChange('clienteId', Number.isInteger(nextValue) && nextValue > 0 ? nextValue : '');
                  }}
                  onClienteSelect={onClienteSelect}
                  placeholder="Buscar cliente por nombre o código"
                />
              </div>
            </LabeledField>
            <LabeledField label="Subcuenta" span="col-span-12 md:col-span-6">
              <input className={fieldClass} value={form.subcuentaId || ''} onChange={(e) => onChange('subcuentaId', e.target.value)} />
            </LabeledField>
            <LabeledField label="Servicio" span="col-span-12 md:col-span-6">
              <input className={fieldClass} value={form.servicioCodigo || ''} onChange={(e) => onChange('servicioCodigo', e.target.value)} placeholder="EXPRESO" />
            </LabeledField>
            <LabeledField label="Numerador" span="col-span-12 md:col-span-6">
              <input type="number" className={fieldClass} value={form.numeradorId || ''} onChange={(e) => onChange('numeradorId', e.target.value)} />
            </LabeledField>
            <LabeledField label="Origen sucursal" span="col-span-12 md:col-span-6">
              <input type="number" className={fieldClass} value={form.origenSucursalId} onChange={(e) => onChange('origenSucursalId', e.target.value)} />
            </LabeledField>
            <LabeledField label="Destino sucursal" span="col-span-12 md:col-span-6">
              <input type="number" className={fieldClass} value={form.destinoSucursalId} onChange={(e) => onChange('destinoSucursalId', e.target.value)} />
            </LabeledField>
            <LabeledField label="Origen localidad" span="col-span-12 md:col-span-6">
              <input type="number" className={fieldClass} value={form.origenLocalidadId || ''} onChange={(e) => onChange('origenLocalidadId', e.target.value)} />
            </LabeledField>
            <LabeledField label="Destino localidad" span="col-span-12 md:col-span-6">
              <input type="number" className={fieldClass} value={form.destinoLocalidadId} onChange={(e) => onChange('destinoLocalidadId', e.target.value)} />
            </LabeledField>
            <LabeledField label="Cantidad / bultos" span="col-span-12 md:col-span-4">
              <input type="number" className={fieldClass} value={form.bultos} onChange={(e) => onChange('bultos', e.target.value)} />
            </LabeledField>
            <LabeledField label="Kilos" span="col-span-12 md:col-span-4">
              <input type="number" className={fieldClass} value={form.kg} onChange={(e) => onChange('kg', e.target.value)} />
            </LabeledField>
            <LabeledField label="Metros cúbicos" span="col-span-12 md:col-span-4">
              <input type="number" className={fieldClass} value={form.m3} onChange={(e) => onChange('m3', e.target.value)} />
            </LabeledField>
            <LabeledField label="Kilo aforado" span="col-span-12 md:col-span-4">
              <input type="number" className={fieldClass} value={form.kgAforado || ''} onChange={(e) => onChange('kgAforado', e.target.value)} />
            </LabeledField>
            <LabeledField label="Valor declarado" span="col-span-12 md:col-span-4">
              <input type="number" className={fieldClass} value={form.valorDeclarado} onChange={(e) => onChange('valorDeclarado', e.target.value)} />
            </LabeledField>
            <LabeledField label="Contrareembolso" span="col-span-12 md:col-span-4">
              <input type="number" className={fieldClass} value={form.contrareembolso} onChange={(e) => onChange('contrareembolso', e.target.value)} />
            </LabeledField>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button type="button" className={buttonClass} onClick={onReset}>Limpiar</button>
            <button type="button" className={primaryButtonClass} onClick={onRun} disabled={isRunning || !hasValidCliente}>
              <FileSearch className="mr-2 h-4 w-4" aria-hidden="true" />
              {isRunning ? 'Simulando...' : 'Simular cálculo'}
            </button>
          </div>
        </section>

        <section className="col-span-12 space-y-4 xl:col-span-7">
          {simulation ? (
            <>
              {diagnostic ? (
                <div className={`${shellCard} p-5`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Diagnóstico operativo</p>
                  <p className="mt-2 text-sm font-semibold text-[rgb(var(--fg))]">{diagnostic.summary}</p>
                  {diagnostic.code ? <p className="mt-1 text-sm text-[rgba(var(--fg),0.82)]">Estado: {diagnostic.code}</p> : null}
                  {(diagnostic.reasons || []).length ? (
                    <ul className="mt-3 space-y-2 text-sm text-[rgba(var(--fg),0.82)]">
                      {diagnostic.reasons.map((reason, index) => (
                        <li key={`${reason}-${index}`} className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] px-3 py-2">
                          {reason}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <p className="mt-3 text-sm text-[rgba(var(--fg),0.82)]">
                    Fallback: {diagnostic.tarifa_general_aplicada ? 'se aplicó una tarifa general compatible.' : diagnostic.sin_tarifa_aplicable ? 'no hubo tarifa aplicable.' : 'no fue necesario; ganó una tarifa específica.'}
                  </p>
                </div>
              ) : null}

              <div className={`${shellCard} p-5`}>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <MetricCard label="Total estimado" value={money(simulation.totalFinal)} tone="brand" />
                  <MetricCard label="Reglas evaluadas" value={String(simulation.reglasEvaluadas || 0)} />
                  <MetricCard label="Coincidencias" value={String(simulation.coincidencias || 0)} />
                  <MetricCard label="Conceptos resueltos" value={String(simulation.detalles?.length || 0)} />
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Regla ganadora</p>
                    <p className="mt-2 text-sm text-[rgb(var(--fg))]">
                      {simulation.explanation?.conceptos?.[0]?.reglaElegida?.ruleCode || 'Sin regla ganadora'}
                    </p>
                    <p className="mt-1 text-sm text-[rgba(var(--fg),0.82)]">
                      {simulation.explanation?.conceptos?.[0]?.reglaElegida
                        ? `Ganó por especificidad ${simulation.explanation.conceptos[0].reglaElegida.score} y prioridad ${simulation.explanation.conceptos[0].reglaElegida.prioridad}.`
                        : 'No hubo reglas que pasaran filtros.'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Versiones candidatas</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(simulation.versionesCandidatas || []).length ? (
                        simulation.versionesCandidatas.map((item) => (
                          <span key={`${item.id}-${item.versionNumero}`} className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.16)] px-3 py-1 text-xs font-semibold text-[rgba(var(--fg),0.84)]">
                            {item.listaCodigo || 'Lista'} / v{item.versionNumero || '-'}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-[rgba(var(--muted),0.95)]">No hubo versiones candidatas.</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={`${shellCard} p-5`}>
                <div className="flex items-center gap-2 text-base font-semibold text-[rgb(var(--fg))]">
                  <CheckCircle2 className="h-5 w-5 text-[rgb(var(--brand))]" aria-hidden="true" />
                  Resultado por concepto
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-[rgba(var(--muted),0.95)]">
                        <th className="pb-2">Concepto</th>
                        <th className="pb-2">Regla aplicada</th>
                        <th className="pb-2">Base</th>
                        <th className="pb-2">Cálculo</th>
                        <th className="pb-2">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(simulation.detalles || []).map((item) => (
                        <tr key={`${item.conceptId}-${item.ruleId}`} className="border-t border-[rgba(var(--border),0.35)]">
                          <td className="py-3">{item.conceptCode} · {item.conceptName}</td>
                          <td>{item.ruleCode || 'Sin regla'}</td>
                          <td>{item.baseCodigo} · {item.baseValor}</td>
                          <td>
                            {item.metodoCalculo === 'PORCENTAJE'
                              ? `${item.porcentaje}%`
                              : `${money(item.valorUnitario)}${item.metodoCalculo === 'POR_UNIDAD' ? ' x base' : ''}`}
                          </td>
                          <td>{money(item.importe)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className={`${shellCard} p-5`}>
                  <div className="flex items-center gap-2 text-base font-semibold text-[rgb(var(--fg))]">
                    <Sparkles className="h-5 w-5 text-[rgb(var(--brand))]" aria-hidden="true" />
                    Timeline del cálculo
                  </div>
                  <ol className="mt-4 space-y-3">
                    {(simulation.timeline || []).map((item, index) => (
                      <TimelineItem key={`${item.key || item.step}-${index}`} item={item} index={index} />
                    ))}
                  </ol>
                </div>

                <div className={`${shellCard} p-5`}>
                  <div className="flex items-center gap-2 text-base font-semibold text-[rgb(var(--fg))]">
                    <ClipboardList className="h-5 w-5 text-[rgb(var(--brand))]" aria-hidden="true" />
                    Regla ganadora por concepto
                  </div>
                  <div className="mt-4 space-y-3">
                    {(simulation.explanation?.conceptos || []).map((item) => (
                      <div key={item.conceptId} className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] p-4 text-sm">
                        <p className="font-semibold text-[rgb(var(--fg))]">{item.conceptCode} · {item.conceptName}</p>
                        <p className="mt-2 text-[rgba(var(--fg),0.84)]">
                          Ganó <strong>{item.reglaElegida?.ruleCode || 'sin regla'}</strong> por especificidad {item.reglaElegida?.score ?? '-'} y prioridad {item.reglaElegida?.prioridad ?? '-'}.
                        </p>
                        <p className="mt-1 text-[rgba(var(--muted),0.95)]">Importe estimado: {money(item.reglaElegida?.importe)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className={`${shellCard} p-5`}>
                  <div className="flex items-center gap-2 text-base font-semibold text-[rgb(var(--fg))]">
                    <SearchX className="h-5 w-5 text-[rgb(var(--brand))]" aria-hidden="true" />
                    Reglas descartadas
                  </div>
                  {(simulation.explanation?.descartes || []).length ? (
                    <ul className="mt-4 space-y-3 text-sm text-[rgba(var(--fg),0.84)]">
                      {simulation.explanation.descartes.map((item, index) => (
                        <li key={`${item.ruleId}-${index}`} className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] p-4">
                          <p className="font-semibold text-[rgb(var(--fg))]">{item.ruleCode}</p>
                          <p className="mt-1">{item.discardReasons?.join(', ') || 'Otra regla más específica ganó.'}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-4 text-sm text-[rgba(var(--muted),0.95)]">No hubo descartes relevantes.</p>
                  )}
                </div>

                <div className={`${shellCard} p-5`}>
                  <div className="flex items-center gap-2 text-base font-semibold text-[rgb(var(--fg))]">
                    <ClipboardList className="h-5 w-5 text-[rgb(var(--brand))]" aria-hidden="true" />
                    Reglas que matchearon
                  </div>
                  {(simulation.explanation?.reglasEvaluadas || []).length ? (
                    <ul className="mt-4 space-y-3 text-sm text-[rgba(var(--fg),0.84)]">
                      {simulation.explanation.reglasEvaluadas.filter((item) => item.matches).map((item) => (
                        <li key={`${item.ruleId}-${item.conceptId}`} className="rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] p-4">
                          <p className="font-semibold text-[rgb(var(--fg))]">{item.ruleCode} · {item.conceptCode}</p>
                          <p className="mt-1">Base {item.base?.code || '-'} · prioridad {item.prioridad} · score {item.score}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-4 text-sm text-[rgba(var(--muted),0.95)]">Todavía no hay reglas matcheadas.</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className={shellCard}>
              <EmptyState
                title="Simulá antes de emitir o revalorizar"
                description="Acá vas a ver el detalle por concepto, la regla ganadora, los descartes y el total estimado sin tocar snapshots reales."
                icon={Calculator}
                action={<button type="button" className={primaryButtonClass} onClick={onRun}>Probar valorización</button>}
              />
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
